import * as React from 'react'
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import { Card } from '@fluentui/react-components'
import { ViewCRMProps } from '../ViewCRM'
import DocumentFlatTree, {
    DocumentFlatTreeMessageKeys,
    FolderDriveItem,
} from '../../../../components/Document/DocumentFlatTree'
import { DocumentSortOrder } from '../../../../config/documentConfig'
import SimpleDocumentFlatTreeItem from '../../../../components/Document/SimpleDocumentFlatTreeItem'
import HammockDialog, { useDialogStyles } from '../../../components/Dialog/HammockDialog'
import PreviewDocumentDialogBody, { PreviewDocumentDialogBodyProps } from '../../../components/Dialog/DriveItem/PreviewDocumentDialogBody'

// The default messages speak of a dossier; on this tab the documents belong to a CRM relation.
const crmMessageKeys: DocumentFlatTreeMessageKeys = {
    noLink: 'crm.document.error.no_link',
    noLinkEdit: 'crm.document.error.no_link.edit',
    folderNotFound: 'crm.document.error.folder_not_found',
    folderNotFoundEdit: 'crm.document.error.folder_not_found.edit',
    folderNoAccess: 'crm.document.error.folder_no_access',
}

export default function CRMDocumentOverview(props: ViewCRMProps): ReactElement {
    const dialogStyles = useDialogStyles()

    const [selectedOptions, setSelectedOptions] = useState<string[]>([])
    const [openPreviewDocument, setOpenPreviewDocument] = useState<FolderDriveItem>()
    const [allDriveItems, setAllDriveItems] = useState<FolderDriveItem[]>(undefined)
    const [filteredDriveItems, setFilteredDriveItems] = useState<FolderDriveItem[]>(undefined)

    const documentDriveItems = useMemo(
        () => (filteredDriveItems ? filteredDriveItems : allDriveItems)?.filter(item => item.itemType === 'leaf'),
        [allDriveItems, filteredDriveItems],
    )

    const reloadDriveItemsFunc = useRef<() => void>(() => {})

    const onOptionsSelect = useCallback((ids: string[]) => {
        setSelectedOptions(ids)
    }, [])

    return (
        <Card>
            <HammockDialog<PreviewDocumentDialogBodyProps>
                open={openPreviewDocument !== undefined}
                setOpen={(open: boolean) => setOpenPreviewDocument(open ? openPreviewDocument : undefined)}
                dialogBody={PreviewDocumentDialogBody}
                surfaceProps={{
                    className: dialogStyles.dialogHeight,
                }}
                dialogBodyProps={() => ({
                    driveItem: openPreviewDocument,
                    callback: () => reloadDriveItemsFunc.current?.(),
                    hasPrevious: () => openPreviewDocument && documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id) > 0,
                    hasNext: () =>
                        openPreviewDocument &&
                        documentDriveItems?.length > documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id) + 1,
                    previous: () => {
                        const curIndex = documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id)
                        if (curIndex > 0) {
                            setOpenPreviewDocument(documentDriveItems?.[curIndex - 1])
                        }
                    },
                    next: () => {
                        const curIndex = documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id)
                        if (documentDriveItems?.length > curIndex) {
                            setOpenPreviewDocument(documentDriveItems?.[curIndex + 1])
                        }
                    },
                })}
            />
            <DocumentFlatTree
                rootId={props.crmRelation.documentRootId}
                messageKeys={crmMessageKeys}
                sortOrder={DocumentSortOrder.NameAsc}
                selectedOptions={selectedOptions}
                onOptionsSelect={onOptionsSelect}
                onDriveItemsLoaded={(allItems, filteredItems) => {
                    setAllDriveItems(allItems)
                    setFilteredDriveItems(filteredItems)
                }}
                setReloadDriveItems={setter => (reloadDriveItemsFunc.current = setter)}
                treeItemContent={(flatTree, treeProps, flatTreeItem, isContentLoading, fileCount) => (
                    <SimpleDocumentFlatTreeItem
                        key={flatTreeItem.value}
                        {...flatTreeItem.getTreeItemProps()}
                        {...treeProps}
                        item={flatTreeItem.getTreeItemProps()}
                        flatTreeHook={flatTree}
                        itemType={flatTreeItem.itemType}
                        size={treeProps.size}
                        isContentLoading={isContentLoading}
                        fileCount={fileCount}
                        onPreviewClick={driveItem => setOpenPreviewDocument(driveItem)}
                    />
                )}
            />
        </Card>
    )
}
